import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { PremiumLock } from "@/components/PremiumLock";
import { ChargeStoryboard } from "@/components/ChargeStoryboard";
import { DeductionLetterVersions } from "@/components/DeductionLetterVersions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Scale, Sparkles, Loader2, Upload, Plus, Trash2, Save, RefreshCw, Info, Check,
  FileText, X, Link2, Download, CheckCircle2,
} from "lucide-react";

const CATEGORIES = ["cleaning", "repair", "paint", "flooring", "trash", "keys", "other"];
const EV_TYPE_LABEL = {
  photo: "Photo", comparison_finding: "Comparison", report_note: "Report", maintenance_issue: "Maintenance",
  communication: "Communication", rent_payment: "Rent", prep_item: "Prep item", third_party: "Third-party",
};

export function DeductionAuditor({ propertyId, archived }) {
  const { isPremium } = useTier();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // 'manual' | null
  const [manualCharges, setManualCharges] = useState([{ label: "", amount: "", landlord_note: "" }]);

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    api.get(`/properties/${propertyId}/deduction-audit`)
      .then(({ data }) => setAudit(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isPremium, propertyId]);

  if (!isPremium) {
    return (
      <PremiumLock
        testId="deduction-auditor-locked"
        feature="deduction_auditor"
        icon={Scale}
        title="Deposit deduction auditor"
        description="Upload or enter your landlord's deduction list and match each charge to your TenantSeal evidence."
      />
    );
  }

  const genManual = async () => {
    const charges = manualCharges
      .filter((c) => c.label.trim())
      .map((c) => ({ label: c.label.trim(), amount: c.amount === "" ? null : Number(c.amount), landlord_note: c.landlord_note.trim() || null }));
    if (!charges.length) { toast.error("Add at least one charge with a label."); return; }
    const fd = new FormData();
    fd.append("source_type", "manual");
    fd.append("charges", JSON.stringify(charges));
    await runCreate(fd);
  };

  const genUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("source_type", "uploaded_document");
    fd.append("file", file);
    await runCreate(fd);
  };

  const runCreate = async (fd) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/deduction-audit`, fd);
      setAudit(data); setMode(null);
      toast.success("Draft audit ready — review each charge, then save");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't organize the charges. Try again.");
    } finally { setBusy(false); }
  };

  const runFromSource = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/deduction-audit/from-source`, body);
      if (data?.fallback) { toast.info(data.fallback); setMode("manual"); return; }
      setAudit(data); setMode(null);
      toast.success("Draft audit ready — review each charge, then save");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create a draft from that record. Try again.");
    } finally { setBusy(false); }
  };

  const patchCharge = async (chargeId, updates) => {
    try {
      const { data } = await api.patch(`/properties/${propertyId}/deduction-audit/charges/${chargeId}`, updates);
      setAudit(data);
    } catch (e) { toast.error(e?.response?.status === 423 ? "Records are finalized (read-only)." : "Couldn't update."); }
  };

  const removeCharge = async (chargeId) => {
    try {
      const { data } = await api.delete(`/properties/${propertyId}/deduction-audit/charges/${chargeId}`);
      setAudit(data);
    } catch { toast.error("Couldn't remove charge."); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/deduction-audit/save`, { response_draft: audit.response_draft });
      setAudit(data); toast.success("Deduction review saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); } finally { setBusy(false); }
  };

  const exportPdf = () => {
    window.open(`${API}/properties/${propertyId}/deduction-audit/export.pdf`, "_blank");
  };

  return (
    <div id="deduction-auditor-card" data-testid="deduction-auditor-card" className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <Scale size={20} className="text-brand" />
        <h2 className="font-heading text-xl font-semibold text-navy">Deposit deduction auditor</h2>
      </div>
      <p className="mt-2 text-sm text-[#475569]">Upload or enter your landlord's deduction list and match each charge to your TenantSeal evidence.</p>

      {loading ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-[#64748B]"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : !audit ? (
        <StartArea propertyId={propertyId} mode={mode} setMode={setMode} manualCharges={manualCharges} setManualCharges={setManualCharges}
          busy={busy} archived={archived} onManual={genManual} onUpload={genUpload} onFromSource={runFromSource} />
      ) : (
        <AuditReview audit={audit} busy={busy} archived={archived} propertyId={propertyId}
          onPatch={patchCharge} onRemove={removeCharge} onSave={save} onExport={exportPdf}
          onRegenerate={() => { setAudit(null); setMode(null); }}
          onDraftChange={(v) => setAudit((a) => ({ ...a, response_draft: v }))} />
      )}

      <DeductionLetterVersions propertyId={propertyId} archived={archived} />

      <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] px-4 py-3 text-xs text-[#64748B]">
        <Info size={15} className="mt-0.5 shrink-0" />
        <span>TenantSeal provides organization and documentation tools, not legal advice. Review every item before saving or exporting.</span>
      </div>
    </div>
  );
}

function StartArea({ propertyId, mode, setMode, manualCharges, setManualCharges, busy, archived, onManual, onUpload, onFromSource }) {
  const setRow = (i, key, val) => setManualCharges((cs) => cs.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)));
  const addRow = () => setManualCharges((cs) => [...cs, { label: "", amount: "", landlord_note: "" }]);
  const delRow = (i) => setManualCharges((cs) => cs.filter((_, idx) => idx !== i));

  if (archived) return <p className="mt-5 text-sm text-[#94A3B8]">This property is finalized (read-only). You can view an existing audit but can't create a new one.</p>;

  if (mode === "sources") {
    return <SourcesView propertyId={propertyId} busy={busy} onBack={() => setMode(null)} onFromSource={onFromSource} />;
  }

  if (mode === "manual") {
    return (
      <div className="mt-5 space-y-3" data-testid="deduction-manual-entry">
        {manualCharges.map((c, i) => (
          <div key={i} className="rounded-xl border border-[#E2E8F0] p-3 space-y-2" data-testid={`manual-charge-${i}`}>
            <div className="flex gap-2">
              <Input data-testid={`manual-charge-label-${i}`} value={c.label} onChange={(e) => setRow(i, "label", e.target.value)}
                placeholder="Charge (e.g. Carpet cleaning)" className="h-10 rounded-lg" />
              <Input data-testid={`manual-charge-amount-${i}`} value={c.amount} onChange={(e) => setRow(i, "amount", e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="$" className="h-10 rounded-lg w-24" inputMode="decimal" />
              {manualCharges.length > 1 && (
                <button data-testid={`manual-charge-del-${i}`} onClick={() => delRow(i)} className="text-[#CBD5E1] hover:text-red-500 px-1"><Trash2 size={16} /></button>
              )}
            </div>
            <Input data-testid={`manual-charge-note-${i}`} value={c.landlord_note} onChange={(e) => setRow(i, "landlord_note", e.target.value)}
              placeholder="Landlord note (optional)" className="h-9 rounded-lg text-sm" />
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <Button data-testid="manual-add-row" variant="outline" size="sm" onClick={addRow} className="rounded-lg"><Plus size={14} className="mr-1.5" /> Add charge</Button>
          <Button data-testid="manual-organize-btn" size="sm" disabled={busy} onClick={onManual} className="rounded-lg bg-navy hover:bg-navy-hover text-white">
            {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />} Organize with AI
          </Button>
          <Button data-testid="manual-cancel-btn" variant="ghost" size="sm" onClick={() => setMode(null)} className="rounded-lg">Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="deduction-start">
      <button data-testid="deduction-manual-btn" onClick={() => setMode("manual")}
        className="rounded-xl border border-[#E2E8F0] hover:border-brand p-4 text-left transition-colors">
        <Plus size={18} className="text-brand" />
        <p className="mt-2 text-sm font-semibold text-navy">Enter charges manually</p>
        <p className="text-xs text-[#64748B]">Type each line from your landlord's list.</p>
      </button>
      <label data-testid="deduction-upload-btn" className={`rounded-xl border border-[#E2E8F0] hover:border-brand p-4 text-left transition-colors cursor-pointer block ${busy ? "opacity-60 pointer-events-none" : ""}`}>
        {busy ? <Loader2 size={18} className="text-brand animate-spin" /> : <Upload size={18} className="text-brand" />}
        <p className="mt-2 text-sm font-semibold text-navy">Upload deduction list</p>
        <p className="text-xs text-[#64748B]">PDF or photo of the itemized statement.</p>
        <input type="file" accept="application/pdf,image/*" className="hidden"
          onChange={(e) => onUpload(e.target.files?.[0])} data-testid="deduction-file-input" />
      </label>
      <button data-testid="deduction-source-btn" onClick={() => setMode("sources")}
        className="rounded-xl border border-[#E2E8F0] hover:border-brand p-4 text-left transition-colors">
        <FileText size={18} className="text-brand" />
        <p className="mt-2 text-sm font-semibold text-navy">Start from existing record</p>
        <p className="text-xs text-[#64748B]">Reuse a past analysis, message, or document.</p>
      </button>
    </div>
  );
}

function SourcesView({ propertyId, busy, onBack, onFromSource }) {
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState(null);
  const [pickedComm, setPickedComm] = useState(null);
  const [pickedDoc, setPickedDoc] = useState(null);

  useEffect(() => {
    api.get(`/properties/${propertyId}/deduction-audit/sources`)
      .then(({ data }) => setSources(data))
      .catch(() => setSources({ deduction_analysis: null, communication_log: [], documents: [] }))
      .finally(() => setLoading(false));
  }, [propertyId]);

  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");
  const hasAny = sources && (sources.deduction_analysis || (sources.communication_log || []).length || (sources.documents || []).length);

  return (
    <div className="mt-5 space-y-4" data-testid="deduction-sources">
      <div className="flex items-center gap-2 rounded-xl bg-[#EFF6FF] border border-[#BFDBFE] px-3 py-2 text-xs text-[#1E40AF]">
        <Info size={14} className="shrink-0" /> TenantSeal can use this record to draft a deduction review. Review everything before saving.
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#64748B]"><Loader2 className="animate-spin" size={16} /> Loading your records…</div>
      ) : !hasAny ? (
        <p className="text-sm text-[#64748B]" data-testid="deduction-sources-empty">No existing records found to seed from. Try manual entry or upload instead.</p>
      ) : (
        <>
          {sources.deduction_analysis && (
            <div className="rounded-xl border border-[#E2E8F0] p-3.5 flex items-center justify-between gap-3" data-testid="source-analysis">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-navy">Use previous deduction analysis</p>
                <p className="text-xs text-[#64748B]">{sources.deduction_analysis.charge_count} charge(s) · {fmt(sources.deduction_analysis.created_at)}</p>
              </div>
              <Button data-testid="source-analysis-btn" size="sm" disabled={busy}
                onClick={() => onFromSource({ source_type: "deduction_analysis", source_id: sources.deduction_analysis.analysis_id })}
                className="rounded-lg bg-navy hover:bg-navy-hover text-white shrink-0">Create draft audit</Button>
            </div>
          )}

          {(sources.communication_log || []).length > 0 && (
            <div data-testid="source-comm">
              <p className="text-sm font-semibold text-navy mb-1.5">Use a communication log message</p>
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {sources.communication_log.map((c) => (
                  <button key={c.entry_id} data-testid={`source-comm-${c.entry_id}`} onClick={() => setPickedComm(c.entry_id)}
                    className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${pickedComm === c.entry_id ? "border-brand bg-brand/[0.05]" : "border-[#E2E8F0] hover:border-brand/50"}`}>
                    <div className="flex items-center gap-2 text-[11px] text-[#94A3B8]">
                      <span>{fmt(c.occurred_at)}</span>{c.channel && <span>· {c.channel}</span>}{c.direction && <span>· {c.direction}</span>}
                    </div>
                    <p className="text-xs text-[#334155] mt-0.5 line-clamp-2">{c.snippet || "(no text)"}</p>
                  </button>
                ))}
              </div>
              <Button data-testid="source-comm-btn" size="sm" disabled={busy || !pickedComm}
                onClick={() => onFromSource({ source_type: "communication_log", source_id: pickedComm })}
                className="mt-2 rounded-lg bg-navy hover:bg-navy-hover text-white">
                {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null} Create draft audit
              </Button>
            </div>
          )}

          {(sources.documents || []).length > 0 && (
            <div data-testid="source-doc">
              <p className="text-sm font-semibold text-navy mb-1.5">Use an uploaded document</p>
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {sources.documents.map((d) => (
                  <button key={d.doc_id} data-testid={`source-doc-${d.doc_id}`} onClick={() => setPickedDoc(d.doc_id)}
                    className={`w-full text-left rounded-lg border px-3 py-2 transition-colors flex items-center gap-2 ${pickedDoc === d.doc_id ? "border-brand bg-brand/[0.05]" : "border-[#E2E8F0] hover:border-brand/50"}`}>
                    <FileText size={14} className="text-[#94A3B8] shrink-0" />
                    <span className="text-xs text-[#334155] truncate flex-1">{d.filename}</span>
                    <span className="text-[11px] text-[#94A3B8]">{fmt(d.uploaded_at)}</span>
                  </button>
                ))}
              </div>
              <Button data-testid="source-doc-btn" size="sm" disabled={busy || !pickedDoc}
                onClick={() => onFromSource({ source_type: "document", source_id: pickedDoc })}
                className="mt-2 rounded-lg bg-navy hover:bg-navy-hover text-white">
                {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null} Create draft audit
              </Button>
            </div>
          )}
        </>
      )}

      <Button data-testid="deduction-sources-back" variant="ghost" size="sm" onClick={onBack} className="rounded-lg">Back</Button>
    </div>
  );
}

function AuditReview({ audit, busy, archived, propertyId, onPatch, onRemove, onSave, onExport, onRegenerate, onDraftChange }) {
  const index = audit.evidence_index || {};
  const saved = audit.status === "saved";
  return (
    <div className="mt-5 space-y-4" data-testid="deduction-audit-review">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span data-testid="deduction-audit-status" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${saved ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEF3C7] text-[#92400E]"}`}>
          {saved ? <Check size={12} /> : <Sparkles size={12} />} {saved ? "Saved" : "Draft — review & save"}
        </span>
        <div className="flex gap-2">
          {!archived && (
            <Button data-testid="deduction-regenerate-btn" variant="outline" size="sm" onClick={onRegenerate} className="rounded-lg"><RefreshCw size={14} className="mr-1.5" /> Start over</Button>
          )}
          <Button data-testid="deduction-export-btn" variant="outline" size="sm" onClick={onExport} className="rounded-lg"><Download size={14} className="mr-1.5" /> Export PDF</Button>
          {!saved && !archived && (
            <Button data-testid="deduction-save-btn" size="sm" disabled={busy} onClick={onSave} className="rounded-lg bg-navy hover:bg-navy-hover text-white"><Save size={14} className="mr-1.5" /> Save review</Button>
          )}
        </div>
      </div>

      {(audit.charges || []).map((c) => (
        <ChargeCard key={c.id} charge={c} index={index} archived={archived} propertyId={propertyId} onPatch={onPatch} onRemove={onRemove} />
      ))}

      <div>
        <p className="text-sm font-semibold text-navy mb-1.5">Response draft (for your review)</p>
        <Textarea data-testid="deduction-response-draft" value={audit.response_draft || ""} onChange={(e) => onDraftChange(e.target.value)}
          disabled={archived} rows={8} className="rounded-xl text-sm" placeholder="Draft wording for your review…" />
        <p className="mt-1 text-xs text-[#94A3B8]">Draft wording for your review. Edit before sending anything. This response does not provide legal advice.</p>
      </div>
    </div>
  );
}

function ChargeCard({ charge: c, index, archived, propertyId, onPatch, onRemove }) {
  const [note, setNote] = useState(c.tenant_response_note || "");
  const [picker, setPicker] = useState(false);
  const matched = (c.matched_evidence_ids || []).map((id) => ({ id, ...index[id] })).filter((m) => m.type);
  const available = Object.entries(index).filter(([id]) => !(c.matched_evidence_ids || []).includes(id));

  const toggleEvidence = (id, add) => {
    const next = add ? [...(c.matched_evidence_ids || []), id] : (c.matched_evidence_ids || []).filter((x) => x !== id);
    onPatch(c.id, { matched_evidence_ids: next });
  };

  return (
    <div className="rounded-xl border border-[#E2E8F0] p-3.5" data-testid={`charge-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-navy">{c.label}</p>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-[#64748B]">
            {c.amount != null && <span className="font-semibold text-navy">${Number(c.amount).toFixed(2)}</span>}
            <select data-testid={`charge-category-${c.id}`} value={c.category} disabled={archived}
              onChange={(e) => onPatch(c.id, { category: e.target.value })}
              className="rounded-md border border-[#E2E8F0] px-1.5 py-0.5 text-xs bg-white">
              {CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            {c.landlord_note && <span className="italic">“{c.landlord_note}”</span>}
          </div>
        </div>
        {!archived && (
          <button data-testid={`charge-delete-${c.id}`} onClick={() => onRemove(c.id)} className="text-[#CBD5E1] hover:text-red-500 shrink-0"><Trash2 size={15} /></button>
        )}
      </div>

      <div className="mt-2.5">
        <p className="text-xs font-semibold text-[#475569]">{matched.length ? "Possible related evidence" : "No matching TenantSeal record found"}</p>
        {matched.length > 0 && (
          <div className="mt-1.5 space-y-1" data-testid={`charge-matched-${c.id}`}>
            {matched.map((m) => (
              <div key={m.id} className="flex items-start gap-1.5 text-xs text-[#334155] bg-[#F8FAFC] rounded-lg px-2 py-1.5">
                <Link2 size={12} className="text-brand mt-0.5 shrink-0" />
                <span className="flex-1 min-w-0"><b>{EV_TYPE_LABEL[m.type] || "Evidence"}:</b> {m.label}{m.detail ? ` — ${m.detail}` : ""}</span>
                {!archived && <button data-testid={`charge-unmatch-${c.id}-${m.id}`} onClick={() => toggleEvidence(m.id, false)} className="text-[#94A3B8] hover:text-red-500"><X size={13} /></button>}
              </div>
            ))}
          </div>
        )}
        {!archived && available.length > 0 && (
          <div className="mt-1.5">
            <button data-testid={`charge-add-evidence-${c.id}`} onClick={() => setPicker((p) => !p)} className="text-xs font-medium text-brand hover:underline inline-flex items-center gap-1">
              <Plus size={12} /> Add matched evidence
            </button>
            {picker && (
              <div className="mt-1.5 max-h-44 overflow-y-auto rounded-lg border border-[#E2E8F0] divide-y divide-[#F1F5F9]" data-testid={`charge-evidence-picker-${c.id}`}>
                {available.map(([id, m]) => (
                  <button key={id} data-testid={`charge-match-option-${c.id}-${id}`} onClick={() => { toggleEvidence(id, true); setPicker(false); }}
                    className="w-full text-left px-2.5 py-1.5 text-xs text-[#334155] hover:bg-[#F8FAFC]">
                    <b>{EV_TYPE_LABEL[m.type] || "Evidence"}:</b> {m.label}{m.detail ? ` — ${m.detail}` : ""}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {c.evidence_gap && (
        <p className="mt-2 text-xs text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-2 py-1.5" data-testid={`charge-gap-${c.id}`}>
          Gap: {c.evidence_gap}
        </p>
      )}

      <ChargeStoryboard charge={c} propertyId={propertyId} index={index} archived={archived} onPatch={onPatch} />

      <div className="mt-2.5">
        <Textarea data-testid={`charge-note-${c.id}`} value={note} onChange={(e) => setNote(e.target.value)}
          onBlur={() => note !== (c.tenant_response_note || "") && onPatch(c.id, { tenant_response_note: note })}
          disabled={archived} rows={2} className="rounded-lg text-sm" placeholder="Your note for this charge (optional)…" />
      </div>
    </div>
  );
}
